import { useCallback, type ReactNode } from 'react';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';

import { useSearchFilter } from './useSearchFilter';
import SearchFilterBase from './SearchFilterBase';
import SearchFilterOptionRow from './SearchFilterOptionRow';
import {
  buildVolumeAutocompleteQuery,
  toVolumeAutocompleteOptions,
  type VolumeAutocompleteField,
} from './volumeAutocomplete';

const autocompleteUrl = '/api/v1/volumes/autocomplete';

export interface VolumeAutocompleteFilterProps {
  filters: SelectOption[];
  setFilters: (selections: SelectOption[]) => void;
  isDisclosureOpen?: boolean;
  accounts?: string;
}

export interface VolumeAutocompleteFilterConfig {
  /** The `field` sent to `/volumes/autocomplete`, and the `group` written onto selections. */
  field: VolumeAutocompleteField;
  /** Heading above the result group. */
  groupLabel: string;
  /** Shown in the results area before the user has typed anything. */
  mustTypeFirstMessage: string;
  /**
   * Seeds the input when the section is opened. The prefix-seeded fields use a real prefix
   * ('eks', 'i-', 'vol-'); node group has no shared prefix and uses a single space instead,
   * which reaches the API as `pattern=` and lists everything — see `allowBlankPattern`.
   */
  defaultQuery: string;
  allowBlankPattern?: boolean;
  debounceMs?: number;
  /** Tag styling on the default row renderer. Ignored when `renderOption` is supplied. */
  tagVariant?: 'cluster' | 'instance' | 'volume' | 'node' | 'default';
  /** Volume ID renders a bespoke checkbox row rather than the shared tag row. */
  renderOption?: (args: {
    option: SelectOption;
    isSelected: boolean;
    isFocused: boolean;
    onSelect: (option: SelectOption) => void;
  }) => ReactNode;
  /** Prefix on the console error when a search fails. */
  logPrefix: string;
}

/**
 * Builds one of the volume-autocomplete filter sections.
 *
 * Cluster Name, Instance ID, Volume ID and Node group were four ~130-line files that differed
 * only in the fields below — adding Node group for FRNT-335 would have made a fourth copy. The
 * request shape and response mapping live in `volumeAutocomplete.ts`, which is covered by
 * `volumeAutocomplete.test.ts`; everything here is the wiring those four shared verbatim.
 *
 * Volume Tags and Account are deliberately NOT built from this: tags parse a `key: value`
 * input into a `tag:<key>` field, and accounts search an in-memory list rather than the API.
 */
export function createVolumeAutocompleteFilter({
  field,
  groupLabel,
  mustTypeFirstMessage,
  defaultQuery,
  allowBlankPattern = false,
  debounceMs = 300,
  tagVariant = 'default',
  renderOption: renderOptionOverride,
  logPrefix,
}: VolumeAutocompleteFilterConfig) {
  function VolumeAutocompleteFilter({
    filters,
    setFilters,
    isDisclosureOpen,
    accounts,
  }: VolumeAutocompleteFilterProps) {
    const api = useXFetch();

    const searchOptions = useCallback(
      async (pattern: string): Promise<GroupedOption[]> => {
        const qs = buildVolumeAutocompleteQuery(field, pattern, accounts, { allowBlankPattern });

        if (qs === null) {
          return [];
        }

        try {
          const response = await api<VolumesAutoCompleteResponse>(`${autocompleteUrl}?${qs}`, {
            // Autocomplete results follow the tenant's volumes, so never serve a cached list.
            // Three of the four filters this replaced already did this; cluster name did not.
            cache: 'no-store',
          });

          return toVolumeAutocompleteOptions(response.values, field, groupLabel);
        } catch (error) {
          console.error(`${logPrefix} Error searching ${groupLabel}:`, error);
          return [];
        }
      },
      [api, accounts]
    );

    const {
      searchResults,
      isLoading,
      inputValue,
      showResults,
      focusedIndex,
      localSelections,
      selectedIdSet,
      flattenedResults,
      inputRef,
      resultsRef,
      handleInputChange,
      handleInputFocus,
      handleInputBlur,
      handleSelect,
      handleResultsMouseDown,
      handleResultsMouseUp,
      handleKeyDown,
    } = useSearchFilter({
      initialSelections: filters,
      onSelectionsChange: setFilters,
      searchFunction: searchOptions,
      filterGroup: field,
      debounceMs,
      defaultQuery,
      isDisclosureOpen,
    });

    const renderOption = useCallback(
      (option: SelectOption, isSelected: boolean, isFocused: boolean = false) =>
        renderOptionOverride ? (
          renderOptionOverride({ option, isSelected, isFocused, onSelect: handleSelect })
        ) : (
          <SearchFilterOptionRow
            option={option}
            isSelected={isSelected}
            onSelect={handleSelect}
            tagVariant={tagVariant}
            isFocused={isFocused}
          />
        ),
      [handleSelect]
    );

    return (
      <div className="flex w-full flex-1 flex-col">
        <SearchFilterBase
          inputValue={inputValue}
          onInputChange={handleInputChange}
          inputRef={inputRef}
          placeholder="Search"
          searchResults={searchResults}
          isLoading={isLoading}
          showResults={showResults}
          focusedIndex={focusedIndex}
          flattenedResults={flattenedResults}
          localSelections={localSelections}
          onSelect={handleSelect}
          selectedIdSet={selectedIdSet}
          renderOption={renderOption}
          className="w-full"
          mustTypeFirstMessage={mustTypeFirstMessage}
          resultsRef={resultsRef}
          onResultsMouseDown={handleResultsMouseDown}
          onResultsMouseUp={handleResultsMouseUp}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
      </div>
    );
  }

  // Otherwise all four filters show up as `VolumeAutocompleteFilter` in the React tree.
  VolumeAutocompleteFilter.displayName = `VolumeAutocompleteFilter(${field})`;

  return VolumeAutocompleteFilter;
}
